import { useEffect, useRef, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { RelativeTime } from '@/components/common/relative-time';
import { ApiErrorCard } from '@/components/feedback/ApiErrorCard';
import { EmptyState } from '@/components/feedback/EmptyState';
import {
  BranchFilter,
  NoFilterResults,
  SearchField,
} from '@/components/filters';
import { PageShell } from '@/components/layout/PageShell';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { OdooSectionNav } from '@/features/odoo/components/OdooSectionNav';
import { ReconciliationTable } from '@/features/odoo/components/ReconciliationTable';
import { ReconciliationTiles } from '@/features/odoo/components/ReconciliationTiles';
import { errorCodeOf } from '@/features/odoo/errors';
import {
  useExportOdooReconciliation,
  useInvalidateReconciliation,
  useOdooPermissions,
  useOdooReconciliation,
  useOdooReconciliationSummary,
  useOdooStatus,
  useRunOdooReconciliation,
} from '@/features/odoo/queries';
import {
  advanceQueuedRun,
  attentionCount,
  ATTENTION_STATUSES,
  describeReconciliationStatus,
  describeRunError,
  hasNeverRun,
  isAllClear,
  queuedRunFrom,
  runButtonState,
  showsOdooUnavailable,
  type QueuedRun,
} from '@/features/odoo/reconciliation';
import {
  DEFAULT_RECONCILIATION_TAB,
  exportFiltersFromSearch,
  RECONCILIATION_PAGE_SIZE,
  reconciliationExportFilename,
  reconciliationFiltersFromSearch,
  ReconciliationSearchSchema,
  tileSearch,
  type ReconciliationSearch,
  type ReconciliationTab,
} from '@/features/odoo/reconciliation-search';
import { syncSearchForStatus } from '@/features/odoo/sync-search';
import {
  RECONCILIATION_STATUSES,
  type ReconciliationCounts,
  type ReconciliationStatus,
} from '@/features/odoo/types';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { useToast } from '@/hooks/use-toast';
import { apiErrorMessage } from '@/lib/api';
import { businessToday } from '@/lib/dates';
import { saveBlob } from '@/lib/download';
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  Info,
  Loader2,
  Play,
  RefreshCw,
} from '@/lib/icons';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_layout/settings/odoo/reconciliation')({
  validateSearch: ReconciliationSearchSchema,
  component: OdooReconciliationPage,
});

const ALL_TAB_KEY = 'odoo.reconciliation.tabs.all';

function tabCount(
  tab: ReconciliationTab,
  counts: ReconciliationCounts | undefined
): number | undefined {
  if (!counts) return undefined;
  if (tab !== 'all') return counts[tab];
  return RECONCILIATION_STATUSES.reduce((sum, s) => sum + counts[s], 0);
}

function TabButton({
  active,
  label,
  count,
  onClick,
}: Readonly<{
  active: boolean;
  label: string;
  count?: number;
  onClick: () => void;
}>) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
        active
          ? 'border-brand-500 bg-brand-50 text-foreground'
          : 'border-border-light bg-card text-muted-foreground hover:bg-surface-container-low/60'
      )}
    >
      {label}
      {count !== undefined ? (
        <span className="rounded-full bg-muted px-1.5 text-[11px] tabular-nums">
          {count}
        </span>
      ) : null}
    </button>
  );
}

function OdooReconciliationPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const { canUpdate } = useOdooPermissions();

  const tab = search.status ?? DEFAULT_RECONCILIATION_TAB;
  const page = search.page ?? 1;
  const filters = reconciliationFiltersFromSearch(search);
  const exportFilters = exportFiltersFromSearch(search);

  const [searchInput, setSearchInput] = useState(search.search ?? '');
  const debouncedSearch = useDebouncedValue(searchInput, 350).trim();

  const [queued, setQueued] = useState<QueuedRun | null>(null);
  const status = useOdooStatus();
  const summary = useOdooReconciliationSummary({ busy: queued !== null });
  const list = useOdooReconciliation(filters);
  const run = useRunOdooReconciliation();
  const exportCsv = useExportOdooReconciliation();
  const invalidate = useInvalidateReconciliation();

  // Every poll, not only changed data: the queued-run grace period runs on the clock.
  useEffect(() => {
    setQueued((q) => advanceQueuedRun(q, summary.data, Date.now()));
  }, [summary.data, summary.dataUpdatedAt]);

  const summaryRunning = summary.data?.running ?? false;
  const busy = summaryRunning || queued !== null;
  const wasBusy = useRef(busy);
  useEffect(() => {
    if (wasBusy.current && !busy) invalidate();
    wasBusy.current = busy;
  }, [busy, invalidate]);

  const buttonState = runButtonState({
    canUpdate,
    requestPending: run.isPending,
    summaryRunning,
    queued,
  });

  const goTo = (next: Partial<ReconciliationSearch>) =>
    navigate({
      to: '/settings/odoo/reconciliation',
      search: (prev) => {
        const merged = { ...prev, page: undefined, ...next };
        return Object.fromEntries(
          Object.entries(merged).filter(
            ([, value]) => value !== undefined && value !== ''
          )
        );
      },
      replace: true,
    });

  const lastPushed = useRef(search.search ?? '');
  useEffect(() => {
    const fromUrl = search.search ?? '';
    if (fromUrl === lastPushed.current) return;
    lastPushed.current = fromUrl;
    setSearchInput(fromUrl);
  }, [search.search]);

  useEffect(() => {
    if (debouncedSearch === lastPushed.current) return;
    lastPushed.current = debouncedSearch;
    void goTo({ search: debouncedSearch });
    // goTo is rebuilt each render; only a settled term should touch the URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const handleRun = () => {
    const snapshot = queuedRunFrom(summary.data, Date.now());
    run.mutate(undefined, {
      onSuccess: () => {
        setQueued(snapshot);
        toast({
          title: t('odoo.reconciliation.run.queued'),
          description: t('odoo.reconciliation.run.queuedBody'),
        });
      },
      onError: (error) => {
        const view = describeRunError(errorCodeOf(error));
        if (view.alreadyRunning) setQueued(snapshot);
        toast({
          variant: view.tone,
          title: t(view.titleKey),
          description: view.descriptionKey
            ? t(view.descriptionKey)
            : apiErrorMessage(error),
        });
      },
    });
  };

  const handleExport = () => {
    exportCsv.mutate(exportFilters, {
      onSuccess: (blob) =>
        saveBlob(blob, reconciliationExportFilename(exportFilters, businessToday())),
      onError: (error) =>
        toast({
          variant: 'destructive',
          title: t('odoo.reconciliation.export.failed'),
          description: apiErrorMessage(error),
        }),
    });
  };

  const refresh = () => {
    void list.refetch();
    void summary.refetch();
  };

  const counts = summary.data?.counts;
  const rows = list.data?.items ?? [];
  const total = list.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / RECONCILIATION_PAGE_SIZE));
  const firstRow = total === 0 ? 0 : (page - 1) * RECONCILIATION_PAGE_SIZE + 1;
  const lastRow = Math.min(page * RECONCILIATION_PAGE_SIZE, total);
  const isFiltered = Boolean(search.branchId || search.search);
  const neverRun = hasNeverRun(summary.data) && queued === null;
  const allClear = isAllClear(summary.data);
  const activeStatus: ReconciliationStatus | null = tab === 'all' ? null : tab;
  const activeView = activeStatus ? describeReconciliationStatus(activeStatus) : null;

  const visibleTabs: ReadonlyArray<ReconciliationTab> = [
    'all',
    ...RECONCILIATION_STATUSES.filter(
      (s) => s !== 'odoo_unavailable' || (counts?.odoo_unavailable ?? 0) > 0 || tab === s
    ),
  ];

  const runButton =
    buttonState === 'hidden' ? null : (
      <Button
        type="button"
        size="sm"
        disabled={buttonState !== 'idle'}
        onClick={handleRun}
        aria-busy={buttonState !== 'idle'}
      >
        {buttonState === 'idle' ? (
          <Play className="h-4 w-4" aria-hidden />
        ) : (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        )}
        {buttonState === 'idle'
          ? t('odoo.reconciliation.run.action')
          : t('odoo.reconciliation.run.running')}
      </Button>
    );

  return (
    <PageShell
      title={t('odoo.reconciliation.title')}
      description={t('odoo.reconciliation.description')}
      actions={
        <>
          <span className="text-xs text-muted-foreground" aria-live="polite">
            {busy ? (
              t('odoo.reconciliation.runningNow')
            ) : summary.data?.lastRunAt ? (
              <>
                {t('odoo.reconciliation.lastRun')}{' '}
                <RelativeTime value={summary.data.lastRunAt} />
              </>
            ) : summary.data ? (
              t('odoo.reconciliation.neverRun')
            ) : null}
          </span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={exportCsv.isPending || neverRun}
            onClick={handleExport}
          >
            {exportCsv.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Download className="h-4 w-4" aria-hidden />
            )}
            {t('odoo.reconciliation.export.action')}
          </Button>
          {runButton}
        </>
      }
    >
      <OdooSectionNav current="reconciliation" />

      {status.data && !status.data.enabled ? (
        <Alert variant="info">
          <Info className="h-4 w-4" aria-hidden />
          <AlertDescription>
            {t('odoo.reconciliation.notEnabled')}{' '}
            <Link to="/settings/odoo" className="font-medium underline underline-offset-2">
              {t('odoo.sync.held.cta')}
            </Link>
          </AlertDescription>
        </Alert>
      ) : null}

      {showsOdooUnavailable(summary.data) ? (
        <Alert variant="warning">
          <AlertTriangle className="h-4 w-4" aria-hidden />
          <AlertDescription>
            <p className="font-medium">
              {(counts?.odoo_unavailable ?? 0) > 0
                ? t('odoo.reconciliation.unavailable.title', {
                    count: counts?.odoo_unavailable ?? 0,
                  })
                : t('odoo.reconciliation.unavailable.lastRunFailed')}
            </p>
            <p className="mt-0.5">{t('odoo.reconciliation.unavailable.body')}</p>
            {summary.data?.lastError ? (
              <p className="mt-1 break-words font-mono text-xs opacity-80">
                {summary.data.lastError}
              </p>
            ) : null}
          </AlertDescription>
        </Alert>
      ) : null}

      {summary.isError ? (
        <ApiErrorCard
          error={summary.error}
          onRetry={() => void summary.refetch()}
          title={t('odoo.reconciliation.summaryLoadFailed')}
        />
      ) : neverRun ? (
        <section className="rounded-lg border border-border-light bg-card shadow-xs">
          <EmptyState
            title={t('odoo.reconciliation.empty.neverRunTitle')}
            description={
              canUpdate
                ? t('odoo.reconciliation.empty.neverRunBody')
                : t('odoo.reconciliation.empty.neverRunReadOnly')
            }
          />
          {runButton ? <div className="flex justify-center pb-8">{runButton}</div> : null}
        </section>
      ) : (
        <>
          <ReconciliationTiles
            summary={summary.data}
            isLoading={summary.isLoading}
            active={activeStatus}
            onSelect={(s) => void goTo(tileSearch(s, search))}
          />

          {allClear && summary.data ? (
            <Alert variant="success" data-testid="all-clear">
              <CheckCircle2 className="h-4 w-4" aria-hidden />
              <AlertDescription>
                <span className="font-medium">
                  {t('odoo.reconciliation.allClear.title')}
                </span>{' '}
                {t('odoo.reconciliation.allClear.body', {
                  count: summary.data.contractsChecked,
                })}
              </AlertDescription>
            </Alert>
          ) : null}

          <section className="overflow-hidden rounded-lg border border-border-light bg-card shadow-xs">
            <div className="space-y-3 border-b border-border-light bg-surface-container-low/60 px-4 py-3 md:px-5 md:py-3.5">
              <div
                role="group"
                aria-label={t('odoo.reconciliation.tabsLabel')}
                className="flex flex-wrap items-center gap-2"
              >
                {visibleTabs.map((value) => (
                  <TabButton
                    key={value}
                    active={value === tab}
                    label={
                      value === 'all'
                        ? t(ALL_TAB_KEY)
                        : t(describeReconciliationStatus(value).labelKey)
                    }
                    count={tabCount(value, counts)}
                    onClick={() => void goTo({ status: value })}
                  />
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <SearchField
                  value={searchInput}
                  onChange={setSearchInput}
                  placeholder={t('odoo.reconciliation.searchPlaceholder')}
                  isPending={searchInput.trim() !== debouncedSearch}
                  className="min-w-[14rem] max-w-md"
                />
                <BranchFilter
                  value={search.branchId}
                  onChange={(branchId) => void goTo({ branchId })}
                />
                <div className="ms-auto flex items-center gap-2 text-xs text-muted-foreground">
                  {list.dataUpdatedAt ? (
                    <span aria-live="off">
                      {t('odoo.sync.updated')}{' '}
                      <RelativeTime value={list.dataUpdatedAt} />
                    </span>
                  ) : null}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={refresh}
                    aria-label={t('odoo.sync.refresh')}
                    title={t('odoo.sync.refresh')}
                  >
                    <RefreshCw
                      className={cn('h-4 w-4', list.isFetching && 'animate-spin')}
                      aria-hidden
                    />
                  </Button>
                </div>
              </div>
              {activeStatus && activeView ? (
                <div
                  className="flex flex-wrap items-start gap-x-3 gap-y-1 text-sm"
                  data-testid="status-guidance"
                >
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                  <div className="min-w-0 flex-1">
                    <p className="text-muted-foreground">{t(activeView.explanationKey)}</p>
                    <p className="font-medium">{t(activeView.nextStepKey)}</p>
                  </div>
                  {activeView.pointsToSyncHealth ? (
                    <Button asChild variant="outline" size="sm">
                      <Link
                        to="/settings/odoo/sync"
                        search={syncSearchForStatus(activeStatus)}
                      >
                        {t('odoo.reconciliation.openSyncHealth')}
                      </Link>
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </div>

            {list.isLoading ? (
              <div className="space-y-2 p-4" aria-busy="true">
                {Array.from({ length: 5 }, (_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : list.isError ? (
              <div className="p-4">
                <ApiErrorCard
                  error={list.error}
                  onRetry={refresh}
                  title={t('odoo.reconciliation.loadFailed')}
                />
              </div>
            ) : rows.length === 0 ? (
              isFiltered ? (
                <div className="p-4">
                  <NoFilterResults
                    isFiltered
                    emptyTitle={t('odoo.reconciliation.empty.noRows')}
                    onReset={() => {
                      setSearchInput('');
                      void navigate({
                        to: '/settings/odoo/reconciliation',
                        search: { status: search.status },
                        replace: true,
                      });
                    }}
                  />
                </div>
              ) : activeStatus &&
                (ATTENTION_STATUSES as ReadonlyArray<string>).includes(activeStatus) &&
                attentionCount(counts) === 0 ? (
                <EmptyState
                  icon={<CheckCircle2 className="h-6 w-6" />}
                  title={t('odoo.reconciliation.allClear.title')}
                  description={t('odoo.reconciliation.empty.attentionBody')}
                />
              ) : (
                <EmptyState
                  title={t('odoo.reconciliation.empty.noRows')}
                  description={
                    activeStatus
                      ? t('odoo.reconciliation.empty.noRowsForStatus')
                      : t('odoo.reconciliation.empty.noRowsBody')
                  }
                />
              )
            ) : (
              <ReconciliationTable rows={rows} odooUrl={status.data?.odooUrl} />
            )}

            {rows.length > 0 ? (
              <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border-light px-4 py-3 md:px-5">
                <span className="text-sm text-muted-foreground">
                  {t('odoo.sync.showing', { from: firstRow, to: lastRow, total })}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => void goTo({ page: page - 1 })}
                  >
                    {t('common.actions.previous')}
                  </Button>
                  <Badge variant="outline" size="sm" className="tabular-nums">
                    {page} / {totalPages}
                  </Badge>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => void goTo({ page: page + 1 })}
                  >
                    {t('common.actions.next')}
                  </Button>
                </div>
              </div>
            ) : null}
          </section>
        </>
      )}
    </PageShell>
  );
}
